import { gql } from "@apollo/client";
import {
    GetTilesByScreen_portal_tilescreens_tile,
    GetTilesByScreen_portal_tilescreens_tile_tiledatasets_tiledatasets_mls,
    GetTilesByScreen_portal_tilescreens_tile_tiledatasets_tiledatavalues_tiledatavalues_mls,
    GetTilesByScreen_portal_tilescreens_tile_tiles_mls
} from "./generated/GetTilesByScreen";
import { GetTilesScreenDashboard, GetTilesScreenDashboard_tile } from "./generated/GetTilesScreenDashboard";

export enum ScreenType {
    DASHBOARD = 101,
    TENANTS = 102,
    FINANCE = 103,
    DOCUMENTS = 104,
    UNIT = 111,
    PERSONS = 120,
    PERSON = 121,
    ACCOUNT = 131,
    APPLIANCES = 140,
    APPLICANCE = 141,
    MAINTENANCES = 150,
    MAINTENANCE = 151,
    TENANT = 160
}

export const GET_TILES_SCREEN_DASHBOARD = gql`
    query GetTilesScreenDashboard(
        $realestateid: uuid!
        $screen: Int!
        $lang: String!
        $tiletypes: [Int!] = [1, 2, 3, 4, 5, 6, 7, 8]
    ) {
        tile: portal_v_tilescreens_dashboard(
            where: {
                realestateid: { _eq: $realestateid }
                tiletype: { _in: $tiletypes }
                screen: { _eq: $screen }
                tilelanguage: { _eq: $lang }
                tilesdatasetlanguage: { _eq: $lang }
                tiledatavaluelanguage: { _eq: $lang }
            }
            order_by: [{ tilescreensort: asc }, { tiletype: asc }, { tiledatasetsort: asc }, { tiledatavaluesort: asc }]
        ) {
            tileid
            realestateid
            tiletype
            charttype
            screen
            tilescreensort
            userid
            rolename
            tilelanguage
            tiletitle
            tiledatasetsort
            tiledatasetid
            tilesdatasetlanguage
            tiledatasetsubtitle
            tiledatasetlabel
            tiledatavaluesort
            tiledatavalueid
            tiledatavaluegroup
            tiledatavaluevalue
            tiledatavaluevaluetype
            tiledatavaluelanguage
            tiledatavaluelabel
            tiledatavaluehint
        }
    }
`;

export const GET_TILE_INFORMATION = gql`
    query GetTileInformation($lang: String = "de", $customerid: uuid!) {
        tilecustomersettings: ums_customersettings(where: { key: { _eq: "PORTAL.TILECONFIGURATION" } }) {
            default
            id
            value
        }

        tiletypes: common_enumerations(
            where: { type: { _eq: 304 }, language: { _eq: $lang } }
            order_by: { key: asc }
        ) {
            id
            key
            label
        }

        aggregatedportaltiles: portal_tiles_aggregate(
            where: { customerid: { _eq: $customerid } }
            distinct_on: tiletype
        ) {
            calculatedtiletypes: nodes {
                tiletype
            }
        }
    }
`;

export const getTilesForDashboard = (data: GetTilesScreenDashboard): GetTilesByScreen_portal_tilescreens_tile[] => {
    let tiles: GetTilesByScreen_portal_tilescreens_tile[] = [];
    if (data) {
        let tileIdMap = new Map<string, GetTilesScreenDashboard_tile[]>();
        const localTiles = data.tile.slice();
        localTiles.forEach((item) => {
            if (!tileIdMap.has(item.tileid)) {
                tileIdMap.set(
                    item.tileid,
                    localTiles.filter((p) => p.tileid === item.tileid)
                );
            }
        });

        tileIdMap.keys().forEach((tileKey) => {
            // flache View wieder hierarchisch strukturieren mittels Gruppieren der Datasets zum gleichen Tile
            let tileDataSetIdMap = new Map<string, any>();
            const tileValues = tileIdMap.get(tileKey);
            tileValues?.forEach((tile) => {
                if (!tileDataSetIdMap.has(tile.tiledatasetid)) {
                    let tileDataSetValueMap = new Map<string, GetTilesScreenDashboard_tile[]>();
                    const valuesOfDataSet = tileValues.filter((p) => p.tiledatasetid === tile.tiledatasetid);
                    let valuesToDisplay: any[] = [];
                    valuesOfDataSet.forEach((dataset) => {
                        // tileDataSetValues zu tileDataSetId
                        if (!tileDataSetValueMap.has(dataset.tiledatavalueid)) {
                            tileDataSetValueMap.set(
                                dataset.tiledatavalueid,
                                valuesOfDataSet.filter((p) => p.tiledatavalueid === dataset.tiledatavalueid)
                            );

                            tileDataSetValueMap.get(dataset.tiledatavalueid)?.forEach((tiledatavalue) =>
                                valuesToDisplay.push({
                                    id: tiledatavalue.tiledatavalueid,
                                    group: tiledatavalue.tiledatavaluegroup,
                                    value: tiledatavalue.tiledatavaluevalue,
                                    sort: tiledatavalue.tiledatavaluesort,
                                    valuetype: tiledatavalue.tiledatavaluevaluetype,
                                    tiledatavalues_mls: [
                                        {
                                            label: tiledatavalue.tiledatavaluelabel,
                                            hint: tiledatavalue.tiledatavaluehint
                                        }
                                    ] as GetTilesByScreen_portal_tilescreens_tile_tiledatasets_tiledatavalues_tiledatavalues_mls[]
                                })
                            );
                        }
                    });

                    const tileDataSet = {
                        __typename: "portal_tiledatasets",
                        id: tile.tiledatasetid,
                        sort: tile.tiledatasetsort,
                        tiledatasets_mls: [
                            {
                                __typename: "portal_tiledatasets_mls",
                                label: tile.tiledatasetlabel,
                                subtitle: tile.tiledatasetsubtitle
                            } as GetTilesByScreen_portal_tilescreens_tile_tiledatasets_tiledatasets_mls
                        ],

                        tiledatavalues: valuesToDisplay
                    };

                    tileDataSetIdMap.set(tile.tiledatasetid, tileDataSet); //bsp. je ein tileDataSet für Historie und für Prognose
                }
            });

            if (tileValues && tileValues?.length > 0) {
                const tile = tileValues[0];
                const tileToDisplay = {
                    realestateid: tile.realestateid,
                    id: tile.tileid,
                    charttype: tile.charttype,
                    tiles_mls: [{ title: tile.tiletitle }] as GetTilesByScreen_portal_tilescreens_tile_tiles_mls[],
                    tiledatasets: tileDataSetIdMap.values().toArray()
                };

                tiles.push(tileToDisplay as GetTilesByScreen_portal_tilescreens_tile);
            }
        });
    }

    return tiles;
};
